import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { TABLE_CAN_REFUELING, TABLE_REFUELING } from '../../Services/TableService';
import PermissionsService, { PERMISSION_REFUELING } from '../../Services/PermissionsService';
import { URL_REFUELING } from '../../router/constants';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import {
  CurrencyIdApiResponse,
  RefuelingsFormValues,
  RefuelingsRecord,
  RefuelingsResponse,
  RefuelingsTableSettingsKeys,
  VatApiResponse,
} from './types';
import {
  get,
  getCurrencyByCustomerId,
  getFormField,
  getRidesByVehicleId,
  getValidation,
  getVatByCustomerId,
  save,
  tabsByPermission,
  update,
} from './RefuelingService';
import RefuelingForm from './RefuelingsForm';
import LoaderService from '../../Services/LoaderService';
import TabComponent from '../TabComponent';
import { IOption } from '../Forms/CmdField';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { CmdTableWithSelectedVehicles } from '../CmdTable/CmdTableWithSelectedVehicles';
import {
  TABLE_REFUELING_ACCOUNTING_TS,
  TABLE_REFUELING_ANOTHER_CURRENCY,
  TABLE_REFUELING_ASSIGNED_TO_RIDE,
  TABLE_REFUELING_ATTACHMENTS,
  TABLE_REFUELING_AVERAGE_CONSUMPTION,
  TABLE_REFUELING_BILL,
  TABLE_REFUELING_CREATED_AT,
  TABLE_REFUELING_CREATED_BY,
  TABLE_REFUELING_CURRENCY,
  TABLE_REFUELING_CUSTOMER,
  TABLE_REFUELING_FUEL_TYPE,
  TABLE_REFUELING_GAS_STATION_NETWORK_ID,
  TABLE_REFUELING_KEY,
  TABLE_REFUELING_LOCKED,
  TABLE_REFUELING_MILE_AGE_DIFF_KM,
  TABLE_REFUELING_MILEAGE,
  TABLE_REFUELING_MODIFIED_AT,
  TABLE_REFUELING_MODIFIED_BY,
  TABLE_REFUELING_NOTE,
  TABLE_REFUELING_PRICE,
  TABLE_REFUELING_PRICE_INC_VAT,
  TABLE_REFUELING_SETTINGS_KEY,
  TABLE_REFUELING_SOURCE,
  TABLE_REFUELING_UNIT_PRICE,
  TABLE_REFUELING_UNIT_PRICE_INC_VAT,
  TABLE_REFUELING_VAT,
  TABLE_REFUELING_VAT_PRICE,
  TABLE_REFUELING_VEHICLE_LICENCE_NUMBER,
  TABLE_REFUELING_VEHICLE_NAME,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import TableDatepicker from '../TableDatepicker';
import useRefuelings from './useRefuelings';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { getTableSettings, handleOrderColumns } from '../CmdTable/CmdTableService';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../store/recoil/TableState';
import { getDayjsLocale, getDefaultLanguage } from '../../Services/LanguageService';
import { ACTIONS_KEY, CHECKBOX_KEY, SECONDARY_ACTIONS_KEY } from '../CmdTable/constants';
import BulkOperations from '../CmdTable/BulkOperations';
import { putRequest } from '../../Services/HttpService';
import ConfirmDialog from '../ConfirmDialog';
import { selectedVehiclesWithBasicInfoSelector } from '../../store/recoil/vehicles';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import Export from './Export';

const checkIfSubpageCanBeOpened = (pathname: string): boolean => {
  return (
    pathname === URL_REFUELING ||
    pathname === `${URL_REFUELING}/add` ||
    pathname.includes(`${URL_REFUELING}/edit`)
  );
};

function Refuelings() {
  const { formatMessage: f } = useIntl();
  const [pathname, navigate] = useLocation();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(
    checkIfSubpageCanBeOpened(pathname)
  );
  const vehicles = useRecoilValue(selectedVehiclesWithBasicInfoSelector);
  const [locked, setLocked] = React.useState<string>('');
  const [, params] = useRoute(`${URL_REFUELING}/edit/:id`);
  const query = useUrlQueryParams();
  const { setDocumentTitle } = useDocumentTitle();
  const tabs = React.useState(
    tabsByPermission([TABLE_REFUELING, TABLE_CAN_REFUELING], TABLE_REFUELING, selectedCustomers)
  )[0];
  const [filterForRequest, setFilterForRequest] = useRecoilState<ICmdTableFilter>(
    filterForRequestAtomFamily(TABLE_REFUELING_KEY)
  );

  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(TABLE_REFUELING_KEY));
  const { columns, columnOrder, columnVisibility } = useRefuelings();
  const datepickerSettings = useRecoilValue<IDatepickerSettings | null>(setTableDatepickerAtom);
  const [minDatepickerTimestamp, setMinDatepickerTimestamp] = React.useState<Date>(new Date());
  const [maxDatepickerTimestamp, setMaxDatepickerTimestamp] = React.useState<Date>(new Date());
  const customers = useRecoilValue(customersAtom);
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const [selectedRows, setSelectedRows] = React.useState<RefuelingsRecord[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const queryClient = useQueryClient();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState<boolean>(false);

  const { id } = params || {};
  const customerId: number | null = query.get('customerId')
    ? Number(query.get('customerId'))
    : null;

  // const accountingTs: string | null = query.get('accountingTs');

  const isCreatingAllowed = PermissionsService.hasAccess(
    PERMISSION_REFUELING,
    ['create'],
    selectedCustomers
  );
  const isEditingAllowed = PermissionsService.hasAccess(
    PERMISSION_REFUELING,
    ['update'],
    selectedCustomers
  );

  const buttons: any = {
    refueling: {
      handle: ['close'],
    },
  };

  if (isCreatingAllowed) {
    buttons.refueling.open = ['addBtn'];
  }
  if (isCreatingAllowed || isEditingAllowed) {
    buttons.refueling.handle.push('save');
  }

  const handleClose = () => {
    navigate(URL_REFUELING);
    setIsSubpageOpened(false);
    setLocked('');
  };

  const updateTable = React.useCallback(() => {
    queryClient.resetQueries({ queryKey: [TABLE_REFUELING_KEY] });
  }, [queryClient]);

  const handleCreate = async (values: RefuelingsFormValues) => {
    LoaderService.showLoader();
    const response = await save(values);
    if (response) {
      handleClose();
      updateTable();
    }
    LoaderService.showLoader(false);
  };

  const handleEdit = async (values: RefuelingsFormValues) => {
    LoaderService.showLoader();
    const response = await update(Number(id), values);
    if (response) {
      handleClose();
      updateTable();
    }
    LoaderService.showLoader(false);
  };

  React.useEffect(() => {
    if (pathname === URL_REFUELING) {
      setIsSubpageOpened(false);
      const documentTitle = `${f({ id: 'menu.refuelingsOverview' })} - ${f({
        id: 'menu.refuelings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === `${URL_REFUELING}/add`) {
      setIsSubpageOpened(true);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({
        id: 'menu.refuelingsOverview',
      })} - ${f({
        id: 'menu.refuelings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (params && params.id) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({
        id: 'menu.refuelingsOverview',
      })} - ${f({
        id: 'menu.refuelings',
      })}`;
      setDocumentTitle(documentTitle);
      setIsSubpageOpened(true);
    }
    const regex = /\/refuelings\/(\d+)/;
    if (regex.test(pathname)) {
      const documentTitle = `${f({
        id: 'menu.refuelingsOverview',
      })} - ${f({
        id: 'menu.refuelings',
      })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, pathname, setIsSubpageOpened]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[],
    customerForSettings: ICustomer[]
  ) => {
    const orderColumns = handleOrderColumns(newOrderColumns, customerForSettings);

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(TABLE_REFUELING_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns, customers);
      return settings;
    }
    return false;
  };

  const getMinDatepickerValue = () => {
    const date = new Date(new Date().setHours(0, 0, 0, 0));
    date.setMonth(date.getMonth() - 24);
    return date;
  };

  const getDatepickerValue = (index: number) => {
    const value =
      !datepickerSettings ||
      !datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS] ||
      !datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS].date[index]
        ? ''
        : datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS].date[index];
    if (value) {
      const defaultLanguage = getDefaultLanguage();
      return dayjs(value).locale(getDayjsLocale(defaultLanguage)).format('D.M.YYYY HH:mm');
    }
    return '';
  };

  const queries = useQueries({
    queries: [
      {
        queryKey: ['refuelingData', params],
        queryFn: () => get(id, customerId),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // enabled: !!(!data && id),
        enabled: !!id,
        staleTime: 0,
        gcTime: 0,
      },
      {
        queryKey: ['refuelingValidation'],
        queryFn: () => getValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isCreatingAllowed || isEditingAllowed,
      },
      {
        queryKey: ['refuelingCmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { data: formData } = queries[0];
  const { data: validationData } = queries[1];
  const { isLoading: isLoadingSettings } = queries[2];

  React.useEffect(() => {
    if (
      datepickerSettings &&
      datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS] &&
      datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS].date &&
      datepickerSettings[TABLE_REFUELING_ACCOUNTING_TS].date.length === 2 // &&
    ) {
      const newMinTimestamp = datepickerSettings.accountingTs.date[0];
      const newMaxTimestamp = datepickerSettings.accountingTs.date[1];

      const isMinDateChanged =
        dayjs(newMinTimestamp).format('DD.MM.YYYY HH:mm') !==
        dayjs(minDatepickerTimestamp).format('DD.MM.YYYY HH:mm');
      const isMaxDateChanged =
        dayjs(newMaxTimestamp).format('DD.MM.YYYY HH:mm') !==
        dayjs(maxDatepickerTimestamp).format('DD.MM.YYYY HH:mm');

      if (isMinDateChanged) {
        setMinDatepickerTimestamp(newMinTimestamp);
      }

      if (isMaxDateChanged) {
        setMaxDatepickerTimestamp(newMaxTimestamp);
      }

      if (isMinDateChanged || isMaxDateChanged) {
        setFilterForRequest((prevFilter: ICmdTableFilter) => ({
          ...prevFilter,
          accountingTs: {
            min: newMinTimestamp,
            max: newMaxTimestamp,
          },
        }));
      }
    }
  }, [datepickerSettings, maxDatepickerTimestamp, minDatepickerTimestamp, setFilterForRequest]);

  const getRideIdOptions = async (
    custId: number,
    vehicleId: number,
    accountingDate?: string
  ): Promise<IOption[] | false> => {
    return getRidesByVehicleId(custId, vehicleId, accountingDate);
  };

  const getFuelTypeIdOptions = async (custId: number): Promise<IOption[] | false> => {
    return getFormField('fuelTypeId', custId);
  };

  const getCurrencyIdOptions = async (custId: number): Promise<IOption[] | false> => {
    return getFormField('secondaryCurrencyId', custId);
  };

  const getCustomerCurrencyIdOption = async (
    custId: number
  ): Promise<CurrencyIdApiResponse | false> => {
    return getCurrencyByCustomerId(custId);
  };

  const getGasStationNetworkIdOptions = async (custId: number): Promise<IOption[] | false> => {
    return getFormField('gasStationNetworkId', custId);
  };

  const getVatOption = async (custId: number): Promise<VatApiResponse | false> => {
    return getVatByCustomerId(custId);
  };

  // Memoizing unstable props
  const allowedRange = React.useMemo(
    () => [getMinDatepickerValue(), new Date(new Date().setHours(23, 59, 59, 999))],
    []
  );

  const sort = React.useMemo(
    () => ({ column: TABLE_REFUELING_ACCOUNTING_TS, direction: 'desc' }),
    []
  );

  const stickyColumns = React.useMemo(() => [ACTIONS_KEY, SECONDARY_ACTIONS_KEY], []);

  const lockedColumns = React.useMemo(
    () => [
      CHECKBOX_KEY,
      TABLE_REFUELING_LOCKED,
      TABLE_REFUELING_VEHICLE_LICENCE_NUMBER,
      TABLE_REFUELING_ASSIGNED_TO_RIDE,
      TABLE_REFUELING_CURRENCY,
      TABLE_REFUELING_NOTE,
      TABLE_REFUELING_VEHICLE_NAME,
      TABLE_REFUELING_VAT,
      TABLE_REFUELING_VAT_PRICE,
      TABLE_REFUELING_FUEL_TYPE,
      TABLE_REFUELING_AVERAGE_CONSUMPTION,
      TABLE_REFUELING_SOURCE,
      TABLE_REFUELING_CREATED_BY,
      TABLE_REFUELING_MILE_AGE_DIFF_KM,
      TABLE_REFUELING_MILEAGE,
      TABLE_REFUELING_ATTACHMENTS,
      TABLE_REFUELING_BILL,
      TABLE_REFUELING_PRICE_INC_VAT,
      TABLE_REFUELING_MODIFIED_BY,
      TABLE_REFUELING_UNIT_PRICE_INC_VAT,
      TABLE_REFUELING_UNIT_PRICE,
      TABLE_REFUELING_PRICE,
      TABLE_REFUELING_GAS_STATION_NETWORK_ID,
      TABLE_REFUELING_CREATED_AT,
      TABLE_REFUELING_MODIFIED_AT,
      TABLE_REFUELING_CUSTOMER,
      TABLE_REFUELING_ANOTHER_CURRENCY,
      ACTIONS_KEY,
      SECONDARY_ACTIONS_KEY,
    ],
    []
  );

  const customNoDataMessage = React.useMemo(
    () => ({
      title: f({ id: 'table.noData.title' }),
      text: f({ id: 'table.noData.text' }),
    }),
    [f]
  );

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_REFUELING}/add`);
  };

  const handleRemove = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    if (selectedRows.length > 0) {
      setIsConfirmDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    const ids: { [customer: number]: number[] } = {};

    selectedRows.forEach((row) => {
      const { customer, id: removeId } = row;

      if (!ids[customer.id]) {
        ids[customer.id] = [];
      }

      ids[customer.id].push(Number(removeId));
    });

    if (Object.keys(ids).length > 0) {
      const response = await putRequest(`/v1/${TABLE_REFUELING}/remove`, { ids });
      if (response) {
        setSelectedRows([]);
        setTotalCount(0);
        updateTable();
      }
    }

    setIsConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleRowChange = (dataTable: RefuelingsRecord[], tableCount: number) => {
    setSelectedRows(dataTable);
    setTotalCount(tableCount);
  };

  return (
    <div>
      <div className="col-12" style={{ display: isSubpageOpened ? 'none' : '' }}>
        <div className="row section page-head">
          <h4>
            {`${f({ id: 'menu.refuelings' })} / ${f({ id: 'menu.refuelingsOverview' })}`}
            <OnlineHelp topic="refuelings" />
          </h4>
        </div>
        {isConfirmDialogOpen && (
          <ConfirmDialog
            title={f({
              id: 'table.confirm.dialog.multiple_remove.title',
            })}
            cancelCallback={handleCancel}
            continueCallback={handleConfirm}
            cancelButtonTitle={f({
              id: 'form.cancel',
            })}
            continueButtonTitle={f({
              id: 'table.deleteBtn',
            })}
          >
            {f({ id: 'table.confirm.dialog.multiple_remove.message' })}
          </ConfirmDialog>
        )}
        {!isSubpageOpened && (
          <TabComponent tabs={tabs}>
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {/* <div> */}
                    {PermissionsService.hasAccess(
                      PERMISSION_REFUELING,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col-12 col-md-auto mr-md-2 mb-2" />
                  <div className="col col-md" />
                  {/* <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" /> */}
                  <div className="col-auto order-4 order-lg-5">
                    {PermissionsService.hasAccess(PERMISSION_REFUELING, ['read']) && <Export />}
                  </div>
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={TABLE_REFUELING_KEY}
                      tableName={TABLE_REFUELING_SETTINGS_KEY}
                      sort={sort}
                      translationKeys={RefuelingsTableSettingsKeys}
                      // locked={[TABLE_REFUELING_LOCKED, TABLE_REFUELING_ACCOUNTING_TS]}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  <TableDatepicker
                    name={TABLE_REFUELING_ACCOUNTING_TS}
                    allowedRange={allowedRange}
                    isActive={true}
                    type="chip"
                  >
                    <div
                      className="label label-primary mr-1 mb-1 label--filter"
                      style={{ cursor: 'pointer', position: 'relative' }}
                      data-cy="datapicker-filter-accountingTs"
                    >
                      <span>
                        {`${f({ id: 'costsOverviewForm.dateTime' })}: ${getDatepickerValue(
                          0
                        )} - ${getDatepickerValue(1)}`}
                      </span>
                    </div>
                  </TableDatepicker>
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={TABLE_REFUELING_KEY} />
                    ))}
                </div>
                {selectedRows.length > 0 && (
                  <BulkOperations<RefuelingsRecord>
                    selectedRows={selectedRows}
                    actions={[
                      {
                        id: 'remove',
                        name: f({ id: 'table_list.actions.remove' }),
                        icon: 'bin',
                        callback: handleRemove,
                      },
                    ]}
                    totalCount={totalCount}
                  />
                )}
                {isLoadingSettings && <CmdLoader inContent={true} />}
                {filterForRequest?.accountingTs && (
                  <CmdTableWithSelectedVehicles<RefuelingsResponse, RefuelingsRecord>
                    name={TABLE_REFUELING_KEY}
                    stickyColumns={stickyColumns}
                    lockedColumns={lockedColumns}
                    columns={columns}
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    customNoDataMessage={customNoDataMessage}
                    leftStickyColumn={[CHECKBOX_KEY]}
                    vehicleKey="vehicleId"
                    onSelectedRowsChange={handleRowChange}
                    isSummaryRequired
                  />
                )}
              </div>
            </section>
          </TabComponent>
        )}
      </div>
      {isSubpageOpened && validationData && !id && !formData && (
        // Object.values(vehicles).length > 0 && (
        <div>
          {PermissionsService.hasAccess(PERMISSION_REFUELING, ['read'], customerId) && (
            <RefuelingForm
              validation={validationData}
              onCancelCallback={handleClose}
              onCreateCallback={handleCreate}
              additionalData={{
                locked,
                getRideIdOptions,
                getCurrencyIdOptions,
                getFuelTypeIdOptions,
                getGasStationNetworkIdOptions,
                vehicles,
                getCustomerCurrencyIdOption,
                getVatOption,
              }}
            />
          )}
        </div>
      )}
      {isSubpageOpened && formData && id && Object.values(vehicles).length > 0 && (
        <div>
          {PermissionsService.hasAccess(PERMISSION_REFUELING, ['update', 'read'], customerId) && (
            <RefuelingForm
              data={formData}
              onCancelCallback={handleClose}
              onEditCallback={handleEdit}
              validation={validationData}
              additionalData={{
                getRideIdOptions,
                getCurrencyIdOptions,
                getFuelTypeIdOptions,
                getGasStationNetworkIdOptions,
                vehicles,
                getCustomerCurrencyIdOption,
                getVatOption,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default Refuelings;
